<template>
  <div v-if="profile" class="ppm" role="dialog" aria-modal="true" @click.self="$emit('close')">
    <div class="ppm__card">
      <button type="button" class="ppm__close" aria-label="بستن" @click="$emit('close')">
        ×
      </button>
      <div class="ppm__identity">
        <div class="ppm__avatar" :class="{ 'ppm__avatar--img': !!avatarSrc }">
          <img v-if="avatarSrc" :src="avatarSrc" alt="" />
          <i v-else class="bi bi-person-fill" aria-hidden="true" />
        </div>
        <div>
          <h3 class="ppm__name">{{ displayName }}</h3>
          <p class="ppm__handle">@{{ profile.user.username }}</p>
          <p class="ppm__status">{{ statusLabel }}</p>
        </div>
      </div>

      <div class="ppm__streak">
        <p class="ppm__streak-title">پیشرفت استریک</p>
        <div class="ppm__stats">
          <div>
            <strong>{{ fa(profile.streak.current_streak) }}</strong>
            <span>استریک فعلی</span>
          </div>
          <div>
            <strong>{{ fa(profile.streak.longest_streak) }}</strong>
            <span>بلندترین</span>
          </div>
          <div>
            <strong>{{ fa(profile.streak.today.sessions_completed) }}/{{ fa(profile.streak.daily_goal) }}</strong>
            <span>هدف امروز</span>
          </div>
          <div>
            <strong>{{ fa(profile.streak.today.xp_earned) }}</strong>
            <span>امتیاز امروز</span>
          </div>
        </div>
      </div>

      <div class="ppm__actions">
        <button
          v-if="profile.friendship_status === 'none'"
          type="button"
          class="ppm__primary"
          :disabled="busy"
          @click="$emit('request', profile.user.username)"
        >
          ارسال درخواست دوستی
        </button>
        <button
          v-else-if="profile.friendship_status === 'incoming' && profile.incoming_request_id"
          type="button"
          class="ppm__primary"
          :disabled="busy"
          @click="$emit('accept', profile.incoming_request_id)"
        >
          پذیرش دوستی
        </button>
        <button
          v-if="profile.friendship_status === 'incoming' && profile.incoming_request_id"
          type="button"
          class="ppm__ghost"
          :disabled="busy"
          @click="$emit('decline', profile.incoming_request_id)"
        >
          رد کردن
        </button>
        <p v-else-if="profile.friendship_status === 'outgoing'" class="ppm__hint">
          درخواست دوستی ارسال شده و در انتظار پاسخ است.
        </p>
        <button
          v-else-if="profile.friendship_status === 'friends'"
          type="button"
          class="ppm__primary"
          :disabled="busy"
          @click="$emit('invite', profile.user.username)"
        >
          دعوت به اسپیکینگ
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { resolveAvatarSrc } from "@/constants/avatars";
import type { PublicProfile } from "@/types";

const props = defineProps<{
  profile: PublicProfile | null;
  busy?: boolean;
}>();

defineEmits(["close", "request", "accept", "decline", "invite"]);

function fa(value: number | string): string {
  return String(value).replace(/\d/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[Number(d)]);
}

const avatarSrc = computed(() => resolveAvatarSrc(props.profile?.user.avatar_url));
const displayName = computed(
  () => props.profile?.user.full_name?.trim() || props.profile?.user.username || ""
);

const statusLabel = computed(() => {
  switch (props.profile?.friendship_status) {
    case "friends":
      return "دوست شما";
    case "incoming":
      return "درخواست دوستی برای شما فرستاده";
    case "outgoing":
      return "در انتظار پذیرش";
    case "self":
      return "پروفایل شما";
    default:
      return "هنوز دوست نیستید — استریک را ببینید و درخواست بفرستید";
  }
});
</script>

<style scoped>
.ppm {
  position: fixed;
  inset: 0;
  z-index: 40;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px;
  background: rgba(44, 32, 24, 0.35);
}

.ppm__card {
  position: relative;
  width: min(420px, 100%);
  background: #fff;
  border: 1px solid #ece4d9;
  border-radius: 24px;
  padding: 22px 20px 18px;
  box-shadow: 0 12px 40px rgba(76, 55, 42, 0.18);
  color: #4c372a;
  font-family: Vazirmatn, system-ui, "Segoe UI", Tahoma, sans-serif;
  direction: rtl;
}

.ppm__close {
  position: absolute;
  top: 10px;
  left: 12px;
  border: none;
  background: none;
  font-size: 1.4rem;
  line-height: 1;
  color: #8c8176;
  cursor: pointer;
}

.ppm__identity {
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 16px;
}

.ppm__avatar {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #fbf7fc;
  color: #6d5ef8;
  display: grid;
  place-items: center;
  font-size: 1.6rem;
  overflow: hidden;
  flex-shrink: 0;
}

.ppm__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.ppm__name {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 700;
}

.ppm__handle {
  margin: 2px 0 0;
  direction: ltr;
  text-align: right;
  color: #6d5ef8;
  font-size: 0.86rem;
  font-weight: 600;
}

.ppm__status {
  margin: 6px 0 0;
  font-size: 0.78rem;
  color: #8c8176;
  line-height: 1.6;
}

.ppm__streak {
  background: #fffdf8;
  border: 1px solid #ece4d9;
  border-radius: 16px;
  padding: 12px;
  margin-bottom: 14px;
}

.ppm__streak-title {
  margin: 0 0 10px;
  font-size: 0.82rem;
  font-weight: 700;
}

.ppm__stats {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.ppm__stats strong {
  display: block;
  font-size: 1.05rem;
  color: #6d5ef8;
}

.ppm__stats span {
  font-size: 0.72rem;
  color: #8c8176;
}

.ppm__actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ppm__primary,
.ppm__ghost {
  width: 100%;
  padding: 10px 12px;
  border-radius: 12px;
  font-family: inherit;
  font-weight: 700;
  font-size: 0.85rem;
  cursor: pointer;
}

.ppm__primary {
  border: none;
  background: #6d5ef8;
  color: #fff;
}

.ppm__ghost {
  border: 1px solid #ece4d9;
  background: #fff;
  color: #4c372a;
}

.ppm__primary:disabled,
.ppm__ghost:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.ppm__hint {
  margin: 0;
  font-size: 0.8rem;
  color: #8c8176;
  text-align: center;
  line-height: 1.7;
}
</style>
